import { type SavedView, fromSavedViewConfig, toSavedViewConfig } from '@/helpers/campaignManagement/savedViews';
import useCampaignManagementSavedViews from '@/hooks/useCampaignManagementSavedViews';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsDV360CampaignManagementEnabled } from '@/store/selectors/currentWhitelabel.selectors';
import { applySavedView } from '@/store/slices/campaignManagementSlice';
import { BookmarkAddOutlined, BookmarkBorderRounded, DeleteOutlineRounded } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { type MouseEvent, useState } from 'react';

const buttonSx = { fontWeight: 600, fontSize: 14, displayPrint: 'none' };

/** Adonis prefixes messages with their code ("E_BAD_REQUEST: ..."); show the sentence only. */
const readableApiError = (error: any, fallback: string): string => {
  const message = error?.response?.data?.message;
  if (typeof message !== 'string' || !message.trim()) return fallback;
  return message.replace(/^E_[A-Z0-9_]+:\s*/, '').trim() || fallback;
};

/** Save the current Campaign Management filters as a named view and reapply them later (#5128). */
const SavedViewsMenu = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(state => state.campaignManagement.filters);
  const clients = useAppSelector(state => state.campaignManagement.clients);
  const isDV360CampaignManagementEnabled = useAppSelector(selectIsDV360CampaignManagementEnabled);
  const { views, save, remove } = useCampaignManagementSavedViews();

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SavedView | null>(null);

  const handleApply = (view: SavedView) => {
    // Clients must be loaded to restore a view's client/advertiser filters.
    if (!clients) return;
    dispatch(applySavedView(fromSavedViewConfig(view.config, clients ?? [], isDV360CampaignManagementEnabled)));
    setAnchor(null);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    // Enter can fire while a save is in flight.
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await save(trimmed, toSavedViewConfig(filters));
      setSaveOpen(false);
      setName('');
    } catch (e) {
      setError(readableApiError(e, 'Could not save this view'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete || busy) return;
    setBusy(true);
    try {
      await remove(pendingDelete.id);
      setPendingDelete(null);
    } catch (e) {
      setError(readableApiError(e, `Could not delete "${pendingDelete.name}"`));
    } finally {
      setBusy(false);
    }
  };

  const askDelete = (view: SavedView) => {
    setError(null);
    setAnchor(null);
    setPendingDelete(view);
  };

  const openSave = () => {
    setError(null);
    setName('');
    setSaveOpen(true);
  };

  return (
    <>
      <Button
        startIcon={<BookmarkBorderRounded />}
        onClick={event => {
          setError(null);
          setAnchor(event.currentTarget);
        }}
        sx={buttonSx}
      >
        Saved views
      </Button>
      <Button startIcon={<BookmarkAddOutlined />} onClick={openSave} sx={buttonSx}>
        Save view
      </Button>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {views.length === 0 ? (
          <MenuItem disabled>
            <Typography variant='body2'>No saved views yet</Typography>
          </MenuItem>
        ) : (
          views.map(view => (
            // Not `disabled` while clients load: that would also block deleting.
            <MenuItem
              key={view.id}
              onClick={() => handleApply(view)}
              onKeyDown={event => {
                if (event.key === 'Delete' || event.key === 'Backspace') askDelete(view);
              }}
              aria-disabled={!clients}
              aria-keyshortcuts='Delete'
            >
              <ListItemText primary={view.name} sx={{ opacity: clients ? 1 : 0.5 }} />
              <IconButton
                size='small'
                tabIndex={-1}
                aria-label={`Delete ${view.name}`}
                onClick={(event: MouseEvent) => {
                  // Otherwise the row's onClick also applies the view being deleted.
                  event.stopPropagation();
                  askDelete(view);
                }}
              >
                <DeleteOutlineRounded fontSize='small' />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Save view</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin='dense'
            label='Name'
            value={name}
            onChange={event => setName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') void handleSave();
            }}
            error={!!error}
            helperText={error ?? 'Saves the current filters, dates and search. Not the table sort.'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={handleSave} disabled={busy || !name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} fullWidth maxWidth='xs'>
        <DialogTitle>Delete saved view</DialogTitle>
        <DialogContent>
          <DialogContentText>Delete "{pendingDelete?.name}"? This can't be undone.</DialogContentText>
          {error ? (
            <Typography variant='body2' sx={{ color: 'error.main', mt: 1 }}>
              {error}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button variant='contained' color='error' onClick={handleDelete} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SavedViewsMenu;
